// applyCatchAllFacetFilter.js

/**
 * Normalizes facet selections into active facet filter descriptors.
 *
 * @param {object} facetSelections
 * @returns {Array<{ fieldKey: string, selectedValue: string, normalizedSelectedValue: string }>}
 */
export const buildActiveCatchAllFacets = (facetSelections) => {
    return Object.entries(facetSelections || {})
        .map(([fieldKey, selectedValue]) => {
            const normalizedFieldKey = String(fieldKey || '').trim();
            const normalizedValue = String(selectedValue || '').trim();
            if (!normalizedFieldKey || !normalizedValue) return null;

            return {
                fieldKey: normalizedFieldKey,
                selectedValue: normalizedValue,
                normalizedSelectedValue: normalizedValue.toLowerCase(),
            };
        })
        .filter(Boolean);
};

/**
 * Resolves the raw value for a lens/facet field on an item.
 *
 * @param {object} item
 * @param {object|null|undefined} fieldDef
 * @param {string} fieldKey
 * @param {function} resolveFieldValue
 * @returns {*}
 */
const resolveFacetRawValue = (item, fieldDef, fieldKey, resolveFieldValue) => {
    if (fieldKey === 'tags') {
        return Array.isArray(item?.tags) ? item.tags : [];
    }

    if (!fieldDef) return undefined;
    return resolveFieldValue?.(item, fieldDef);
};

/**
 * Whether an item matches one active facet selection.
 *
 * @param {object} item
 * @param {object} facet
 * @param {object} params
 * @returns {boolean}
 */
const itemMatchesFacet = (item, facet, {
    resolveFieldValue,
    schemaFieldMap,
}) => {
    const fieldDef = schemaFieldMap?.get?.(facet.fieldKey) || null;
    if (facet.fieldKey !== 'tags' && !fieldDef) return true;

    const rawValue = resolveFacetRawValue(item, fieldDef, facet.fieldKey, resolveFieldValue);

    if (Array.isArray(rawValue)) {
        return rawValue.some((entry) => (
            String(entry || '').trim().toLowerCase() === facet.normalizedSelectedValue
        ));
    }

    return String(rawValue || '').trim().toLowerCase() === facet.normalizedSelectedValue;
};

/**
 * Filters node rows by active catch-all lens/facet selections.
 *
 * @param {Array} items
 * @param {Array} activeCatchAllFacets
 * @param {object} params
 * @param {function} params.resolveFieldValue
 * @param {Map} params.schemaFieldMap
 * @returns {Array}
 */
export default function applyCatchAllFacetFilter(items, activeCatchAllFacets, {
    resolveFieldValue,
    schemaFieldMap,
}) {
    const baseItems = Array.isArray(items) ? items : [];
    const facets = Array.isArray(activeCatchAllFacets) ? activeCatchAllFacets : [];

    if (!facets.length) return baseItems;

    return baseItems.filter((item) => {
        return facets.every((facet) => itemMatchesFacet(item, facet, {
            resolveFieldValue,
            schemaFieldMap,
        }));
    });
}
